import NewsletterAPI from '../../api'
import { showNotification } from 'aldoo-ra/Notification'

/**
 * SubscriberController
 * Handles all subscriber-related operations
 */
class SubscriberController {
  /**
   * Export subscribers to CSV
   */
  async exportSubscribers(selectedFields) {
    try {
      const fields = Object.keys(selectedFields).filter(key => selectedFields[key])

      const response = await NewsletterAPI.exportSubscribers({ fields })

      if (!response.success) {
        throw new Error(response.message || 'Failed to export subscribers')
      }

      // Create and download CSV file
      const blob = new Blob([response.csvData], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `newsletter-subscribers-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      showNotification({
        message: 'Subscribers exported successfully',
        className: 'bg-admin_success text-white'
      })

      return { success: true }
    } catch (error) {
      console.error('Export error:', error)
      showNotification({
        message: `Export failed: ${error.message}`,
        className: 'bg-admin_error text-white'
      })
      return { success: false, error: error.message }
    }
  }

  /**
   * Analyze CSV file before import
   */
  async analyzeImport(file) {
    try {
      const response = await NewsletterAPI.analyzeSubscribersImport(file)

      if (!response.success) {
        throw new Error(response.message || 'Failed to analyze file')
      }

      return {
        success: true,
        stats: response.stats
      }
    } catch (error) {
      console.error('Analysis error:', error)
      showNotification({
        message: `Analysis failed: ${error.message}`,
        className: 'bg-admin_error text-white'
      })
      return { success: false, error: error.message }
    }
  }

  /**
   * Import subscribers from CSV
   */
  async importSubscribers(file, importAsSubscribed) {
    try {
      const response = await NewsletterAPI.importSubscribers({
        file,
        importAsSubscribed
      })

      if (!response.success) {
        throw new Error(response.message || 'Failed to import subscribers')
      }

      showNotification({
        message: `Successfully imported ${response.imported} subscribers`,
        className: 'bg-admin_success text-white'
      })

      return {
        success: true,
        results: response
      }
    } catch (error) {
      console.error('Import error:', error)
      showNotification({
        message: `Import failed: ${error.message}`,
        className: 'bg-admin_error text-white'
      })
      return { success: false, error: error.message }
    }
  }

  /**
   * Add a new subscriber
   */
  async addSubscriber(subscriberData) {
    try {
      const response = await NewsletterAPI.addSubscriber(subscriberData)

      if (!response.success) {
        throw new Error(response.message || 'Failed to add subscriber')
      }

      showNotification({
        message: 'Subscriber added successfully',
        className: 'bg-admin_success text-white'
      })

      return { success: true, subscriber: response.subscriber }
    } catch (error) {
      console.error('Add subscriber error:', error)
      showNotification({
        message: `Failed to add subscriber: ${error.message}`,
        className: 'bg-admin_error text-white'
      })
      return { success: false, error: error.message }
    }
  }

  /**
   * Delete subscribers
   */
  async deleteSubscribers(subscriberIds) {
    try {
      const response = await NewsletterAPI.deleteSubscribers(subscriberIds)

      if (!response.success) {
        throw new Error(response.message || 'Failed to delete subscribers')
      }

      showNotification({
        message: `Successfully deleted ${subscriberIds.length} subscriber(s)`,
        className: 'bg-admin_success text-white'
      })

      return { success: true }
    } catch (error) {
      console.error('Delete error:', error)
      showNotification({
        message: `Delete failed: ${error.message}`,
        className: 'bg-admin_error text-white'
      })
      return { success: false, error: error.message }
    }
  }

  /**
   * Update subscriber
   */
  async updateSubscriber(subscriberId, updates) {
    try {
      const response = await NewsletterAPI.updateSubscriber(subscriberId, updates)

      if (!response.success) {
        throw new Error(response.message || 'Failed to update subscriber')
      }

      showNotification({
        message: 'Subscriber updated successfully',
        className: 'bg-admin_success text-white'
      })

      return { success: true, subscriber: response.subscriber }
    } catch (error) {
      console.error('Update subscriber error:', error)
      showNotification({
        message: `Failed to update subscriber: ${error.message}`,
        className: 'bg-admin_error text-white'
      })
      return { success: false, error: error.message }
    }
  }
}

export default new SubscriberController()
